'use client';

import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import type { MPARequest } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { BackupPolicyTag } from '@/components/backupPolicy/BackupPolicyTag';
import { CloudProviderCircle } from '@/components/cloudProviderIndicator';
import { Panels } from '@/components/panels/panels';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContextTypes';
import { parseJson } from '@/utils/string';
export const getBackupPolicyId = (request: MPARequest | null | undefined) => {
  const path = request?.requestPath;
  if (!path) {
    return null;
  }
  const match = /\/(?:backup-policies|policies)\/([^/?]+)/.exec(path);
  return match?.[1] ?? null;
};
export const getResourceId = (request: MPARequest | null | undefined) => {
  const path = request?.requestPath;
  if (!path) {
    return null;
  }
  const match = /\/resources\/([^/?]+)/.exec(path);
  return match?.[1] ?? null;
};
interface RestoreAccountInfo {
  cloudProvider?: Provider;
  accountId: string;
}
const getRestoreAccountInfo = (request: MPARequest | null | undefined): RestoreAccountInfo | null => {
  const body = parseJson<{
    restoreAccountAttributes?: {
      cloudProvider?: Provider;
      aws?: {
        roleArn?: string;
      };
      gcp?: {
        serviceAccount?: string;
      };
      azure?: {
        subscriptionId?: string;
      };
      mongoAtlas?: {
        atlasProjectId?: string;
      };
    };
  }>(request?.requestBody);
  const attrs = body?.restoreAccountAttributes;
  if (!attrs) {
    return null;
  }
  const cloudProvider = attrs.cloudProvider;
  switch (cloudProvider) {
    case Provider.Aws:
      {
        const roleArn = attrs.aws?.roleArn;
        const accountId = roleArn ? /arn:aws:iam::(\d+):/.exec(roleArn)?.[1] ?? roleArn : '';
        return accountId ? {
          cloudProvider,
          accountId
        } : null;
      }
    case Provider.Azure:
      {
        const accountId = attrs.azure?.subscriptionId ?? '';
        return accountId ? {
          cloudProvider,
          accountId
        } : null;
      }
    case Provider.Gcp:
      {
        const serviceAccount = attrs.gcp?.serviceAccount ?? '';
        const projectMatch = /@([^.]+)\.iam\.gserviceaccount\.com$/.exec(serviceAccount);
        const accountId = projectMatch?.[1] ?? serviceAccount;
        return accountId ? {
          cloudProvider,
          accountId
        } : null;
      }
    case Provider.MongoAtlas:
      {
        const accountId = attrs.mongoAtlas?.atlasProjectId ?? '';
        return accountId ? {
          cloudProvider,
          accountId
        } : null;
      }
    default:
      return null;
  }
};
export const getBackupPolicyNameFromState = (request: MPARequest | null | undefined) => {
  const state = request?.resourceStateBefore;
  if (!state) {
    return null;
  }
  return parseJson<{
    name?: string;
  }>(state)?.name ?? null;
};
export const BackupPolicyEntity = ({
  policyId,
  fallbackName
}: {
  policyId: string | null;
  fallbackName?: string | null;
}) => {
  const dal = useDAL();
  const {
    data,
    isLoading
  } = dal.backupPolicy.useList();
  if (!policyId) {
    return null;
  }
  if (isLoading) {
    return <Typography variant='subtitle1' sx={{
      color: 'text.disabled'
    }}>
        Loading...
      </Typography>;
  }
  const policy = data?.backupPolicies?.find(p => p.id === policyId);
  if (!policy) {
    return <Typography variant='subtitle1' sx={{
      color: 'text.disabled'
    }}>
        {fallbackName ?? policyId}
      </Typography>;
  }
  return <BackupPolicyTag policy={policy} />;
};
export const ResourceEntity = ({
  resourceId
}: {
  resourceId: string | null;
}) => {
  const dal = useDAL();
  const {
    rightPanel
  } = useWorkspace();
  const {
    data,
    isLoading
  } = dal.inventory.useResource(resourceId ?? '');
  if (!resourceId) {
    return null;
  }
  if (isLoading) {
    return <Typography variant='body2' sx={{
      color: 'text.disabled'
    }}>
        Loading...
      </Typography>;
  }
  const resource = data?.resource;
  const displayName = resource?.resourceName || resourceId;
  const openResourcePanel = () => {
    rightPanel.setIsOpen(true, PanelSize.Large);
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resourceId,
        initialTab: 'overview',
        onClose: () => rightPanel.setIsOpen(false)
      }
    });
  };
  return <Stack direction='row' sx={{
    alignItems: 'center',
    gap: '8px'
  }}>
      {resource?.cloudProvider && <CloudProviderCircle cloudProvider={resource.cloudProvider} />}
      <Typography data-testid='resource-name-cell' className='underline hover:cursor-pointer hover:text-(--mui-palette-text-secondary)' onClick={openResourcePanel} sx={{
      lineHeight: '24px',
      position: 'relative'
    }}>
        {displayName}
      </Typography>
    </Stack>;
};
export const RestoreAccountEntity = t0 => {
  const $ = _c(10);
  const {
    request
  } = t0;
  let t1;
  if ($[0] !== request) {
    t1 = getRestoreAccountInfo(request);
    $[0] = request;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const info = t1;
  if (!info) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      alignItems: "center",
      gap: "8px"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== info.cloudProvider) {
    t3 = info.cloudProvider && <CloudProviderCircle cloudProvider={info.cloudProvider} />;
    $[3] = info.cloudProvider;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== info.accountId) {
    t4 = <Typography variant="subtitle2">{info.accountId}</Typography>;
    $[5] = info.accountId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <Stack direction="row" sx={t2}>{t3}{t4}</Stack>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};